import { forwardRef } from "react";
import type { ComponentRef, ForwardedRef, ReactElement, Ref } from "react";
import { InputNumber } from "antd";
import type { InputNumberProps } from "antd";

type InputNumberRef = ComponentRef<typeof InputNumber>;

/**
 * ``InputNumber`` with a mobile-friendly keypad on Android. AntD renders the
 * field as ``role="spinbutton"`` with no ``inputmode`` attribute, so without
 * this the OS pops the full QWERTY keyboard.
 *
 * Keypad choice:
 *   - **Default ``inputMode="decimal"``** — a digit keypad WITH a decimal
 *     separator. This is the SAFE default: it never blocks fractional entry
 *     (e.g. range filters / prices that don't declare ``precision``), and for
 *     integer fields the unused separator is harmless.
 *   - **Auto-promote to ``inputMode="numeric"``** (strict digits-only) when the
 *     field declares itself integer — ``precision={0}`` or an integer ``step``.
 *   - A caller can always override ``inputMode`` explicitly (e.g. ``"none"``
 *     where a custom on-screen keypad owns input).
 *
 * iOS is out of scope for now (its numeric/decimal keypads differ, and lack a
 * minus key for signed fields); we target Android. A future enhancement could
 * branch on UA.
 *
 * ``forwardRef`` so AntD ``<Form.Item>`` — which injects a ref into its child
 * for focus / ``scrollToFirstError`` — keeps working and doesn't emit the
 * "Function components cannot be given refs" warning. Drop-in for antd's
 * ``InputNumber``; imported aliased as ``InputNumber`` so call sites are
 * unchanged. (The stocktake count input keeps antd's component directly — it
 * has its own ref + custom mobile keypad — see StocktakeDetailPage.)
 */
function NumInputInner<T extends number | string = number>(
  props: InputNumberProps<T>,
  ref: ForwardedRef<InputNumberRef>,
) {
  const integerShaped =
    props.precision === 0 ||
    (props.step !== undefined && Number.isInteger(Number(props.step)));
  return (
    <InputNumber<T>
      ref={ref}
      inputMode={integerShaped ? "numeric" : "decimal"}
      {...props}
    />
  );
}

export const NumInput = forwardRef(NumInputInner) as <
  T extends number | string = number,
>(
  props: InputNumberProps<T> & { ref?: Ref<InputNumberRef> },
) => ReactElement;
